import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { interpolar } from "@/momentos";
import api from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

// Divide un párrafo en tramos normales/dorados según [[ ]].
function tramos(parrafo) {
  const out = [];
  const re = /\[\[(.+?)\]\]/g;
  let last = 0, m;
  while ((m = re.exec(parrafo))) {
    if (m.index > last) out.push({ t: parrafo.slice(last, m.index), gold: false });
    out.push({ t: m[1], gold: true });
    last = m.index + m[0].length;
  }
  if (last < parrafo.length) out.push({ t: parrafo.slice(last), gold: false });
  return out;
}

const CHAR_MS = 55;
const PAUSA_MS = 950;

export default function MomentoConciencia({ momento, vars, onCerrar }) {
  const parrafos = momento.parrafos.map((p) => interpolar(p, vars));
  const [visibles, setVisibles] = useState(0);        // párrafos ya completos
  const [parcial, setParcial] = useState("");         // texto que se está escribiendo
  const [terminado, setTerminado] = useState(false);
  const [mostrarSaltear, setMostrarSaltear] = useState(false);
  const [respuesta, setRespuesta] = useState("");
  const inicio = useRef(Date.now());
  const timers = useRef([]);
  const scrollRef = useRef(null);

  // Video opcional del momento (ej. m7 = "empleado_nuevo"): se muestra ARRIBA del texto, sin autoplay.
  const [videoInfo, setVideoInfo] = useState(null);
  useEffect(() => {
    if (!momento.video) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/clips-especiales");
        if (alive) setVideoInfo((data.clips_info || {})[momento.video] || null);
      } catch (e) { /* sin video: solo texto */ }
    })();
    return () => { alive = false; };
  }, [momento.video]);

  const limpiar = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  // La pantalla acompaña el texto SIEMPRE: la última línea escrita queda a la vista.
  // Scroll instantáneo (no "smooth"): con el tipeo a 30ms/char un scroll suave nunca
  // alcanza a llegar al fondo y el texto se escribe fuera de vista (sobre todo en celular).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [parcial, visibles, terminado]);

  useEffect(() => {
    const t = setTimeout(() => setMostrarSaltear(true), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancel = false;
    async function escribir() {
      for (let i = 0; i < parrafos.length; i++) {
        const texto = parrafos[i];
        for (let c = 1; c <= texto.length; c++) {
          if (cancel) return;
          await new Promise((r) => timers.current.push(setTimeout(r, CHAR_MS)));
          if (cancel) return;
          setParcial(texto.slice(0, c));
        }
        if (cancel) return;
        setVisibles(i + 1);
        setParcial("");
        await new Promise((r) => timers.current.push(setTimeout(r, PAUSA_MS)));
      }
      if (!cancel) setTerminado(true);
    }
    escribir();
    return () => { cancel = true; limpiar(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cerrar = useCallback((salteado) => {
    limpiar();
    const seg = Math.round((Date.now() - inicio.current) / 100) / 10;
    onCerrar({ salteado, segundos: seg, respuesta: respuesta.trim() });
  }, [onCerrar, respuesta]);

  const Parrafo = ({ texto, escribiendo }) => (
    <p className="mo-parrafo" style={{ margin: "0 0 1.1em 0", lineHeight: 1.6 }}>
      {tramos(texto).map((tr, k) => (
        <span key={k} style={{ color: tr.gold ? "#C9A84C" : "#EDEDED" }}>{tr.t}</span>
      ))}
      {escribiendo && <span className="mo-cursor" />}
    </p>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        ref={scrollRef}
        className="fixed inset-0 z-[85] overflow-y-auto px-6"
        style={{ background: "#0A0A0A" }}
        data-testid={`momento-${momento.id}`}
      >
        <div className="w-full min-h-full flex flex-col items-center justify-center py-16 mx-auto" style={{ maxWidth: 680 }}>
          {videoInfo && (
            <div className="w-full mb-8 rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(201,168,76,0.35)", background: "#000" }} data-testid={`momento-${momento.id}-video`}>
              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                {videoInfo.tipo === "url" ? (
                  <iframe src={videoInfo.embed_url} title="Video" frameBorder="0" allow="fullscreen; picture-in-picture" allowFullScreen className="absolute top-0 left-0 w-full h-full" data-testid={`momento-${momento.id}-video-iframe`} />
                ) : (
                  <video src={`${API}/api/clip-video/${videoInfo.id}`} controls playsInline className="absolute top-0 left-0 w-full h-full" data-testid={`momento-${momento.id}-video-player`} />
                )}
              </div>
            </div>
          )}
          <div className="font-serif text-center mo-texto" style={{ fontSize: "clamp(24px, 3.2vw, 34px)", color: "#EDEDED" }}>
            {parrafos.slice(0, visibles).map((p, i) => <Parrafo key={i} texto={p} />)}
            {!terminado && parcial !== "" && <Parrafo texto={parcial} escribiendo />}
            {!terminado && parcial === "" && visibles < parrafos.length && <span className="mo-cursor" />}
          </div>

          <AnimatePresence>
            {terminado && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full flex flex-col items-center mt-8">
                {momento.input && (
                  <textarea
                    className="font-sans w-full mb-5 rounded-xl px-4 py-3"
                    style={{ background: "#0F0F0F", border: "1px solid #262626", color: "#EDEDED", fontSize: 16, minHeight: 90, maxWidth: 560, resize: "vertical" }}
                    placeholder={momento.inputPlaceholder || ""}
                    value={respuesta}
                    onChange={(e) => setRespuesta(e.target.value)}
                    data-testid={`momento-${momento.id}-input`}
                    autoFocus
                  />
                )}
                <button
                  onClick={() => cerrar(false)}
                  className="font-sans rounded-full px-10 py-3 transition-transform hover:scale-[1.03]"
                  style={{ background: "#C9A84C", color: "#0A0A0A", fontSize: 15, fontWeight: 600, letterSpacing: "0.04em" }}
                  data-testid={`momento-${momento.id}-boton`}
                >
                  {momento.boton}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {mostrarSaltear && !terminado && (
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} whileHover={{ opacity: 1 }}
            onClick={() => cerrar(true)}
            className="font-sans fixed"
            style={{ right: 24, bottom: 20, fontSize: 12, color: "#8A8A8A", background: "transparent", border: "none" }}
            data-testid={`momento-${momento.id}-saltear`}
          >
            saltear
          </motion.button>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
